import { useCallback, useEffect, useRef, useState } from "react";

import { ApiHttpError, apiJson } from "../lib/api";
import type {
  AdminUserActionResult,
  AdminUserDeleteResult,
  AdminUserSummary,
  CreateAdminUserInput,
} from "../types/adminUsers";

const PAGE_SIZE = 25;
const SEARCH_DELAY_MS = 300;

type DirectoryResponse = {
  users: AdminUserSummary[];
  paging: { first: number; max: number };
};

type CreatedUserResponse = {
  status: "created";
  user: {
    keycloakId: string;
    localUserId: string;
    email: string | null;
    displayName: string;
    enabled: boolean;
    provisioningStatus: "provisioned";
    credentialMode: "invitation" | "temporary_password";
    invitationStatus?: "sent" | "not_sent";
    temporaryPasswordStatus?: "configured";
  };
};

type ProvisionedUserResponse = {
  status: "provisioned";
  user: {
    keycloakId: string;
    localUserId: string;
    email: string | null;
    displayName: string;
    enabled: boolean;
    provisioningStatus: "provisioned";
  };
};

type PartialCreationPayload = {
  status?: string;
  keycloakUserId?: string;
  localUserId?: string;
};

export function useAdminUsers() {
  const [query, setQueryValue] = useState("");
  const [first, setFirst] = useState(0);
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [pendingUserIds, setPendingUserIds] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);
  const requestVersion = useRef(0);
  const abortController = useRef<AbortController | null>(null);
  const operationStatuses = useRef(new Map<string, AdminUserSummary["operationStatus"]>());

  const loadUsers = useCallback(async (search: string, offset: number) => {
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    abortController.current?.abort();
    const controller = new AbortController();
    abortController.current = controller;
    setIsLoading(true);
    setError(null);
    setErrorStatus(null);

    const params = new URLSearchParams({
      query: search.trim(),
      first: `${offset}`,
      max: `${PAGE_SIZE}`,
    });

    try {
      const response = await apiJson<DirectoryResponse>(`/api/admin/keycloak-users?${params}`, {
        signal: controller.signal,
      });
      if (version !== requestVersion.current) return;
      const nextUsers = response.users.map((user) => {
        const operationStatus = operationStatuses.current.get(user.keycloakId);
        if (!operationStatus) return user;
        return { ...user, operationStatus };
      });
      setUsers(nextUsers);
      setHasNextPage(response.users.length === PAGE_SIZE);
    } catch (loadError) {
      if (controller.signal.aborted) return;
      if (version !== requestVersion.current) return;
      setError(readAdminUsersError(loadError, "annuaire"));
      setErrorStatus(readHttpStatus(loadError));
    } finally {
      if (version === requestVersion.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void loadUsers(query, first);
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timeout);
  }, [first, loadUsers, query]);

  useEffect(() => {
    return () => abortController.current?.abort();
  }, []);

  function setQuery(value: string) {
    setQueryValue(value);
    setFirst(0);
  }

  async function createUser(input: CreateAdminUserInput): Promise<AdminUserActionResult> {
    if (isCreating) throw new Error("La création est déjà en cours.");
    setIsCreating(true);

    try {
      const response = await apiJson<CreatedUserResponse>("/api/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      const operationStatus = response.user.invitationStatus === "sent" ? "invitation_sent" : undefined;
      const user = summaryFromCreatedUser(response.user, input, operationStatus);
      if (operationStatus) operationStatuses.current.set(user.keycloakId, operationStatus);
      insertUser(user);
      return {
        user,
        message: creationSuccessMessage(response.user.credentialMode, response.user.invitationStatus),
      };
    } catch (createError) {
      const partial = readPartialCreation(createError, input);
      if (partial) {
        const operationStatus = partial.user.operationStatus;
        if (operationStatus) operationStatuses.current.set(partial.user.keycloakId, operationStatus);
        insertUser(partial.user);
        return partial;
      }
      throw new Error(readAdminUsersError(createError, "création"));
    } finally {
      setIsCreating(false);
    }
  }

  async function provisionUser(user: AdminUserSummary): Promise<AdminUserActionResult> {
    return await runUserAction(user.keycloakId, async () => {
      const response = await apiJson<ProvisionedUserResponse>(
        `/api/admin/users/${encodeURIComponent(user.keycloakId)}/provision`,
        { method: "POST" },
      );
      const provisioned = summaryFromProvisionedUser(response.user, user);
      operationStatuses.current.delete(user.keycloakId);
      replaceUser(provisioned);
      return {
        user: provisioned,
        message: "Provisionnement local terminé. Les accès peuvent maintenant être modifiés.",
      };
    });
  }

  async function resendInvitation(user: AdminUserSummary): Promise<AdminUserActionResult> {
    return await runUserAction(user.keycloakId, async () => {
      await apiJson(`/api/admin/users/${encodeURIComponent(user.keycloakId)}/resend-invitation`, {
        method: "POST",
      });
      const updated = { ...user, operationStatus: "invitation_sent" as const };
      operationStatuses.current.set(user.keycloakId, "invitation_sent");
      replaceUser(updated);
      return {
        user: updated,
        message: "Invitation renvoyée. Keycloak prend en charge la définition du mot de passe.",
      };
    });
  }

  async function resetTemporaryPassword(user: AdminUserSummary, temporaryPassword: string): Promise<AdminUserActionResult> {
    return await runUserAction(user.keycloakId, async () => {
      await apiJson(`/api/admin/users/${encodeURIComponent(user.keycloakId)}/reset-temporary-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ temporaryPassword }),
      });
      const updated = { ...user, operationStatus: undefined };
      operationStatuses.current.delete(user.keycloakId);
      replaceUser(updated);
      return { user: updated, message: "Le nouveau mot de passe temporaire est configuré." };
    });
  }

  async function setUserEnabled(user: AdminUserSummary, enabled: boolean): Promise<AdminUserActionResult> {
    return await runUserAction(user.keycloakId, async () => {
      const action = enabled ? "enable" : "disable";
      await apiJson(`/api/admin/users/${encodeURIComponent(user.keycloakId)}/${action}`, { method: "POST" });
      const updated = { ...user, enabled };
      replaceUser(updated);
      return {
        user: updated,
        message: enabled ? "Le compte a été réactivé." : "Le compte a été désactivé. Ses accès Datavis sont conservés.",
      };
    });
  }

  async function deleteUser(user: AdminUserSummary): Promise<AdminUserDeleteResult> {
    return await runDeleteAction(user.keycloakId, async () => {
      try {
        await apiJson(`/api/admin/users/${encodeURIComponent(user.keycloakId)}`, { method: "DELETE" });
        operationStatuses.current.delete(user.keycloakId);
        setUsers((current) => current.filter((item) => item.keycloakId !== user.keycloakId));
        return { deleted: true, message: "L’utilisateur et ses accès Datavis ont été supprimés." };
      } catch (error) {
        const payload = error instanceof ApiHttpError ? readObject(error.payload) : null;
        if (payload?.status !== "keycloak_deletion_failed") throw error;
        const partial = {
          ...user,
          localUserId: null,
          enabled: false,
          provisioningStatus: "not_provisioned" as const,
          operationStatus: "deletion_failed" as const,
        };
        operationStatuses.current.set(user.keycloakId, "deletion_failed");
        replaceUser(partial);
        return {
          deleted: false,
          message: "Les accès locaux ont été supprimés, mais l’identité Keycloak reste désactivée. Relancez la suppression.",
        };
      }
    });
  }

  async function runDeleteAction(keycloakUserId: string, action: () => Promise<AdminUserDeleteResult>) {
    setPendingUserIds((current) => new Set(current).add(keycloakUserId));
    try {
      return await action();
    } catch (actionError) {
      throw new Error(readAdminUsersError(actionError, "action"));
    } finally {
      setPendingUserIds((current) => removeSetValue(current, keycloakUserId));
    }
  }

  async function runUserAction(
    keycloakUserId: string,
    action: () => Promise<AdminUserActionResult>,
  ) {
    setPendingUserIds((current) => new Set(current).add(keycloakUserId));
    try {
      return await action();
    } catch (actionError) {
      throw new Error(readAdminUsersError(actionError, "action"));
    } finally {
      setPendingUserIds((current) => removeSetValue(current, keycloakUserId));
    }
  }

  function insertUser(user: AdminUserSummary) {
    setUsers((current) => [user, ...current.filter((item) => item.keycloakId !== user.keycloakId)]);
  }

  function replaceUser(user: AdminUserSummary) {
    setUsers((current) => current.map((item) => item.keycloakId === user.keycloakId ? user : item));
  }

  return {
    users,
    query,
    first,
    pageSize: PAGE_SIZE,
    isLoading,
    error,
    errorStatus,
    hasNextPage,
    isCreating,
    pendingUserIds,
    setQuery,
    previousPage: () => setFirst((current) => Math.max(0, current - PAGE_SIZE)),
    nextPage: () => setFirst((current) => current + PAGE_SIZE),
    refresh: () => loadUsers(query, first),
    createUser,
    provisionUser,
    resendInvitation,
    resetTemporaryPassword,
    setUserEnabled,
    deleteUser,
  };
}

function readPartialCreation(
  error: unknown,
  input: CreateAdminUserInput,
): AdminUserActionResult | null {
  if (!(error instanceof ApiHttpError)) return null;
  const payload = readObject(error.payload) as PartialCreationPayload | null;
  if (!payload?.keycloakUserId) return null;

  if (payload.status === "local_provisioning_failed") {
    return {
      user: pendingSummary(payload.keycloakUserId, null, input, "provisioning_failed"),
      message: "Le compte Keycloak a été créé, mais ses accès Datavis n’ont pas pu être initialisés.",
    };
  }

  if (payload.status === "created_invitation_failed" && payload.localUserId) {
    return {
      user: pendingSummary(payload.keycloakUserId, payload.localUserId, input, "invitation_failed"),
      message: "Le compte et ses accès ont été créés, mais l’email n’a pas été envoyé.",
    };
  }

  if (payload.status === "created_temporary_password_failed" && payload.localUserId) {
    return {
      user: pendingSummary(payload.keycloakUserId, payload.localUserId, input, "temporary_password_failed"),
      message: "Le compte et ses accès ont été créés, mais le mot de passe temporaire n’a pas pu être configuré.",
    };
  }

  return null;
}

function pendingSummary(
  keycloakId: string,
  localUserId: string | null,
  input: CreateAdminUserInput,
  operationStatus: "provisioning_failed" | "invitation_failed" | "temporary_password_failed",
): AdminUserSummary {
  return {
    keycloakId,
    localUserId,
    username: input.username,
    email: input.email ?? null,
    firstName: null,
    lastName: null,
    displayName: input.username,
    enabled: true,
    emailVerified: false,
    provisioningStatus: localUserId ? "provisioned" : "not_provisioned",
    operationStatus,
  };
}

function summaryFromCreatedUser(
  response: CreatedUserResponse["user"],
  input: CreateAdminUserInput,
  operationStatus: "invitation_sent" | undefined,
): AdminUserSummary {
  return {
    keycloakId: response.keycloakId,
    localUserId: response.localUserId,
    username: input.username,
    email: response.email,
    firstName: null,
    lastName: null,
    displayName: response.displayName,
    enabled: response.enabled,
    emailVerified: false,
    provisioningStatus: response.provisioningStatus,
    operationStatus,
  };
}

function creationSuccessMessage(
  credentialMode: "invitation" | "temporary_password",
  invitationStatus: "sent" | "not_sent" | undefined,
) {
  if (credentialMode === "temporary_password") {
    return "Utilisateur créé avec un mot de passe temporaire à modifier lors de la première connexion.";
  }
  if (invitationStatus === "sent") {
    return "Utilisateur créé. Keycloak a envoyé le lien de définition du mot de passe.";
  }
  return "Utilisateur créé sans invitation, car aucune adresse email n’a été renseignée.";
}

function summaryFromProvisionedUser(
  response: ProvisionedUserResponse["user"],
  previous: AdminUserSummary,
): AdminUserSummary {
  return {
    ...previous,
    localUserId: response.localUserId,
    email: response.email,
    displayName: response.displayName,
    enabled: response.enabled,
    provisioningStatus: response.provisioningStatus,
    operationStatus: undefined,
  };
}

export function readAdminUsersError(error: unknown, context: "annuaire" | "création" | "action") {
  if (!(error instanceof ApiHttpError)) {
    if (error instanceof TypeError) return "Erreur réseau. Vérifiez votre connexion puis réessayez.";
    return "Une erreur inattendue est survenue.";
  }

  const errorCode = readPayloadErrorCode(error.payload);
  if (errorCode === "KEYCLOAK_PASSWORD_POLICY_REJECTED") return "Le mot de passe temporaire ne respecte pas la politique Keycloak.";
  if (errorCode === "CANNOT_MODIFY_CURRENT_USER" || errorCode === "CANNOT_DELETE_CURRENT_USER") return "Votre propre compte est protégé.";
  if (errorCode === "CANNOT_MODIFY_GLOBAL_ADMIN" || errorCode === "CANNOT_DELETE_GLOBAL_ADMIN") return "Les administrateurs globaux doivent être gérés directement dans Keycloak.";
  if (error.status === 400) return "Les données saisies sont invalides.";
  if (error.status === 401) return "Votre session a expiré. Reconnectez-vous.";
  if (error.status === 403) return "Accès refusé : le rôle datavis_admin est requis.";
  if (error.status === 409) return "Cette adresse ou ce compte existe déjà.";
  if (error.status === 502) return context === "annuaire"
    ? "L’annuaire Keycloak est temporairement indisponible. Les accès locaux restent consultables."
    : "Keycloak ou le service d’invitation est temporairement indisponible.";
  if (error.status === 503) return "Le service d’administration est désactivé ou incomplet.";
  return "Le service a renvoyé une réponse inattendue.";
}

function readHttpStatus(error: unknown) {
  if (error instanceof ApiHttpError) return error.status;
  return null;
}

function readObject(value: unknown) {
  if (!value || typeof value !== "object") return null;
  return value as Record<string, unknown>;
}

function readPayloadErrorCode(value: unknown) {
  const payload = readObject(value);
  const error = readObject(payload?.error);
  return typeof error?.code === "string" ? error.code : null;
}

function removeSetValue(values: Set<string>, value: string) {
  const next = new Set(values);
  next.delete(value);
  return next;
}
